import { PricingInput, type PricingResult, calculatePricing } from '@ebayds/shared';
import { Calculator, Check, Sparkles, TriangleAlert, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { apiPost } from '../api/client.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Field, Input } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { AIBlocks, type AIBlock } from '../components/AIBlocks.js';
import { Skeleton } from '../components/Skeleton.js';

interface Values {
  name: string;
  supplierCostUsd: number;
  shippingCostUsd: number;
  fxRate: number;
  ebayFeePct: number;
  paypalFlatCad: number;
  paypalPctCad: number;
  desiredProfitCad: number;
}

export function PriceCalcPage(): JSX.Element {
  const { register, control } = useForm<Values>({
    defaultValues: {
      name: '',
      supplierCostUsd: 18,
      shippingCostUsd: 0,
      fxRate: 1.36,
      ebayFeePct: 0.13,
      paypalFlatCad: 0.3,
      paypalPctCad: 0.029,
      desiredProfitCad: 8,
    },
  });
  const values = useWatch({ control });

  const result = useMemo<PricingResult | null>(() => {
    const parsed = PricingInput.safeParse({
      supplierCostUsd: values.supplierCostUsd ?? 0,
      shippingCostUsd: values.shippingCostUsd ?? 0,
      fxRate: values.fxRate ?? 1.36,
      ebayFeePct: values.ebayFeePct ?? 0.13,
      paypalFlatCad: values.paypalFlatCad ?? 0.3,
      paypalPctCad: values.paypalPctCad ?? 0.029,
      desiredProfitCad: values.desiredProfitCad ?? 8,
    });
    return parsed.success ? calculatePricing(parsed.data) : null;
  }, [values]);

  const [aiBlocks, setAiBlocks] = useState<AIBlock[] | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const askAI = async (): Promise<void> => {
    setAiLoading(true);
    setAiError(null);
    setAiBlocks(null);
    try {
      const res = await apiPost<{ blocks: AIBlock[] }>('/api/ai/pricing', {
        state: { ...values, ...result },
      });
      setAiBlocks(res.blocks);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'AI request failed.';
      setAiError(
        msg.includes('No AI provider configured')
          ? 'AI is not configured yet. Open Admin and pick a provider and paste your API key.'
          : msg,
      );
    } finally {
      setAiLoading(false);
    }
  };

  const verdictMeta = result
    ? result.verdict === 'good'
      ? {
          bg: 'var(--c-success-bg)',
          fg: 'var(--c-success)',
          icon: Check,
          label: 'Healthy margin — list it',
        }
      : result.verdict === 'warn'
        ? {
            bg: 'var(--c-warn-bg)',
            fg: 'var(--c-warn)',
            icon: TriangleAlert,
            label: 'Tight margin — consider raising price',
          }
        : {
            bg: 'var(--c-danger-bg)',
            fg: 'var(--c-danger)',
            icon: X,
            label: 'Bad — find a cheaper supplier',
          }
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Price"
        accent="calculator"
        icon={<Calculator size={20} color="var(--primary)" strokeWidth={2.2} />}
        sub="Find the store price that hits your profit target after eBay and PayPal fees."
      />

      <Card title="Inputs">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 14,
          }}
        >
          <Field label="Product (optional)">
            <Input {...register('name')} />
          </Field>
          <Field label="Supplier cost USD">
            <Input
              type="number"
              {...register('supplierCostUsd', { valueAsNumber: true })}
              step={0.5}
            />
          </Field>
          <Field label="Shipping cost USD">
            <Input
              type="number"
              {...register('shippingCostUsd', { valueAsNumber: true })}
              step={0.5}
            />
          </Field>
          <Field label="USD → CAD rate">
            <Input
              type="number"
              {...register('fxRate', { valueAsNumber: true })}
              step={0.01}
            />
          </Field>
          <Field label="eBay fee %">
            <Input
              type="number"
              {...register('ebayFeePct', { valueAsNumber: true })}
              step={0.005}
            />
          </Field>
          <Field label="PayPal flat CAD">
            <Input
              type="number"
              {...register('paypalFlatCad', { valueAsNumber: true })}
              step={0.05}
            />
          </Field>
          <Field label="PayPal % CAD">
            <Input
              type="number"
              {...register('paypalPctCad', { valueAsNumber: true })}
              step={0.001}
            />
          </Field>
          <Field label="Desired profit CAD">
            <Input
              type="number"
              {...register('desiredProfitCad', { valueAsNumber: true })}
              step={1}
            />
          </Field>
        </div>
      </Card>

      {result && verdictMeta && (
        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--c-muted)' }}>
                Set your store price at
              </div>
              <div
                style={{
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  marginTop: 4,
                }}
              >
                ${result.storePriceCad.toFixed(2)} CAD
              </div>
              <div style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)', marginTop: 4 }}>
                Profit ${result.profitCad.toFixed(2)} · margin {(result.marginPct * 100).toFixed(1)}%
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: verdictMeta.bg,
                color: verdictMeta.fg,
                fontSize: 'var(--text-md)',
                fontWeight: 600,
              }}
            >
              <verdictMeta.icon size={16} />
              {verdictMeta.label}
            </div>
          </div>
        </Card>
      )}

      <Card
        title="Ask AI to optimize"
        description="Claude reviews your inputs and the verdict, suggests fee/price tweaks."
        action={
          <Button icon={Sparkles} loading={aiLoading} disabled={!result} onClick={askAI}>
            {aiBlocks ? 'Regenerate' : 'Get pricing advice'}
          </Button>
        }
      >
        {aiLoading && <Skeleton height={100} />}
        {aiError && (
          <div
            style={{
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              fontSize: 'var(--text-md)',
            }}
          >
            {aiError}
          </div>
        )}
        {aiBlocks && <AIBlocks blocks={aiBlocks} />}
      </Card>
    </div>
  );
}
